import {Component, Object3D} from "@wonderlandengine/api";
import {HandTracking} from "@wonderlandengine/components";
import {vec3, quat} from "gl-matrix";
import {property} from "@wonderlandengine/api/decorators.js";
import Logger from './utils/logger.js';

const MODULE = 'InputProfile';

const _TempVec = vec3.create();
const _TempQuat = quat.create();
const minTemp = new Float32Array(3);
const maxTemp = new Float32Array(3);
const hands = ["left", "right"];

export class InputProfile extends Component {
	static TypeName = "input-profile";

	private _gamepadObjects: Record<string, any> = {};
	private _missingNodeLogKeys: Set<string> = new Set();
	private controllerModel: any;
	private toFilter: Set<string> = new Set(["vr-mode-active-mode-switch"]);
	private defaultControllerComponents?: Component[];
	private handedness!: string;
	private url!: string;
	private ProfileJSON: any;
	private modelLoaded!: boolean;
	private gamepad: any;

	@property.object()
	defaultController!: Object3D;

	@property.enum(hands, 0)
	handednessIndex: number = 0;

	@property.string(
		"https://cdn.jsdelivr.net/npm/@webxr-input-profiles/assets@latest/dist/profiles/",
	)
	path!: string;

	@property.string()
	customProfileFolder!: string;

	@property.bool(false)
	mapToDefaultController!: boolean;

	@property.object()
	trackedHand!: Object3D;

	init() {
		this._gamepadObjects = {};
	}

	start() {
		this.controllerModel = null;
		this.toFilter = new Set(["vr-mode-active-mode-switch"]);
		this.defaultControllerComponents = this.getComponents(
			this.defaultController,
		);
		this.handedness = hands[this.handednessIndex];

		if (this.engine.xr?.session != null) {
			this.engine.xr?.session.addEventListener(
				"inputsourceschange",
				this.onInputSourcesChange.bind(this),
			);
		} else {
			this.engine.onXRSessionStart.add(() => {
				this.engine.xr?.session.addEventListener(
					"inputsourceschange",
					this.onInputSourcesChange.bind(this),
				);
			});
		}
	}

	addToFilter(element: string) {
		this.toFilter.add(element);
	}

	removeFromFilter(element: string) {
		this.toFilter.delete(element);
	}

	onDeactivate() {
		if (this.engine.xr?.session != null) {
			this.engine.xr.session.removeEventListener(
				"inputsourceschange",
				this.onInputSourcesChange.bind(this),
			);
		} else {
			this.engine.onXRSessionStart.add(() => {
				this.engine.xr?.session.removeEventListener(
					"inputsourceschange",
					this.onInputSourcesChange.bind(this),
				);
			});
		}
	}

	setHandTrackingControllers(controllerObject: any) {
		/**@ts-ignore**/
		this.trackedHand.getComponent(HandTracking).controllerToDeactivate =
			controllerObject;
	}

	getComponents(obj: any) {
		if (obj == null) return;

		const components: Component[] = [];

		const stack = [obj];

		while (stack.length > 0) {
			const currentObj = stack.pop();
			const comps = currentObj
				.getComponents()
				.filter((c: any) => !this.toFilter.has(c.type));
			components.push(...comps);
			const children = currentObj.children || [];
			// Push children onto the stack in reverse order to maintain the correct order
			for (let i = children.length - 1; i >= 0; --i) {
				stack.push(children[i]);
			}
		}

		return components;
	}

	setComponentsActive(active: boolean) {
		const comps = this.defaultControllerComponents;
		if (!comps) return;
		for (let i = 0; i < comps.length; ++i) {
			comps[i].active = active;
		}
	}

	onInputSourcesChange(event: any) {
		if (this.modelLoaded && !this.mapToDefaultController) {
			this.setComponentsActive(false);
		}

		event.added.forEach((xrInputSource: any) => {
			if (xrInputSource.hand != null) return;
			if (this.handedness === xrInputSource.handedness) {
				var profile = this.path + xrInputSource.profiles[0];
				if (this.customProfileFolder !== "") {
					profile = this.customProfileFolder;
				}

				this.url = profile + "/profile.json";
				Logger.debug(`Loading profile from URL: ${this.url}`, MODULE);

				fetch(this.url)
					.then((res) => res.json())
					.then((out) => {
						this.ProfileJSON = out;
						Logger.debug('Profile JSON loaded', MODULE, this.ProfileJSON);
						const statusMessage = this.modelLoaded
							? `Profile loaded: ${this.url}`
							: 'Reloaded Profile for gamepad mapping';
						Logger.debug(statusMessage, MODULE);
						this.loadGamepad(profile, xrInputSource);
					})
					.catch((e) => {
						Logger.error('Failed to load profile.json', MODULE, e);
					});
			}
		});
	}

	isModelLoaded() {
		return this.controllerModel !== null;
	}

	loadGamepad(profile: string, xrInputSource: any) {
		this.gamepad = xrInputSource.gamepad;

		const assetPath = profile + "/" + this.handedness + ".glb";
		if (this.modelLoaded) return;
		Logger.debug(`mapToDefaultController flag: ${this.mapToDefaultController}`, MODULE);

		if (!this.mapToDefaultController) {
			this.engine.scene
				.append(assetPath)
				.then((obj: any) => {
					this.controllerModel = obj;
					this.setComponentsActive(false);
					Logger.debug('Setting components inactive', MODULE);
					this.controllerModel.parent = this.object;
					this.controllerModel.setTranslationLocal([0, 0, 0]);
					this.getGamepadObjectsFromProfile(
						this.ProfileJSON,
						this.controllerModel,
					);
					this.modelLoaded = this.isModelLoaded();
					this.setHandTrackingControllers(this.controllerModel);
					this.update = () => this.mapGamepadInput();
					Logger.info('Controller model loaded to scene', MODULE);
				})
				.catch((e) => {
					Logger.error('Failed to load 3D controller model', MODULE, e);
					this.setComponentsActive(true);
					Logger.debug('Setting components active (fallback)', MODULE);
				});
		} else {
			this.controllerModel = this.defaultController;
			this.getGamepadObjectsFromProfile(
				this.ProfileJSON,
				this.defaultController,
			);
			this.modelLoaded = this.isModelLoaded();
			this.update = () => this.mapGamepadInput();
			this.setHandTrackingControllers(this.defaultController);
		}
	}

	getGamepadObjectsFromProfile(profile: any, obj: any) {
		// Clear previous gamepad objects cache to avoid cross-hand contamination
		this._gamepadObjects = {};
		this._missingNodeLogKeys = new Set();
		
		const components = profile.layouts[this.handedness].components;
		if (!components) {
			console.warn(`[InputProfile] No components in profile for hand=${this.handedness}`);
			return;
		}

		// Collect all available nodes in the controller model
		const allModelNodes = new Set<string>();
		this.collectAllNodeNames(obj, allModelNodes);
		
		// Process requested component nodes
		const requestedNodeNames = new Set<string>();

		for (const i in components) {
			if (components.hasOwnProperty(i)) {
				const visualResponses = components[i].visualResponses;

				for (const j in visualResponses) {
					if (visualResponses.hasOwnProperty(j)) {
						const valueNode = visualResponses[j].valueNodeName;
						const minNode = visualResponses[j].minNodeName;
						const maxNode = visualResponses[j].maxNodeName;

						// Skip components that don't have corresponding nodes in the model
						// This handles cases like menu button on left controller which the profile defines but model doesn't have
						if (!allModelNodes.has(valueNode) || !allModelNodes.has(minNode) || !allModelNodes.has(maxNode)) {
							continue;
						}

						requestedNodeNames.add(valueNode);
						requestedNodeNames.add(minNode);
						requestedNodeNames.add(maxNode);

						this.getGamepadObjectByName(obj, valueNode);
						this.getGamepadObjectByName(obj, minNode);
						this.getGamepadObjectByName(obj, maxNode);
					}
				}
			}
		}
	}

	private collectAllNodeNames(obj: any, nodeSet: Set<string>) {
		if (!obj) return;
		if (obj.name) nodeSet.add(obj.name);
		const children = obj.children;
		if (children) {
			for (let i = 0; i < children.length; ++i) {
				this.collectAllNodeNames(children[i], nodeSet);
			}
		}
	}

	getGamepadObjectByName(obj: any, name: string) {
		if (!obj || !name) return;

		if (obj.name === name) this._gamepadObjects[name] = obj;

		const children = obj.children;
		for (let i = 0; i < children.length; ++i)
			this.getGamepadObjectByName(children[i], name);
	}

	assignTransform(target: any, min: any, max: any, value: number, contextKey?: string) {
		// Guard against undefined nodes (can happen if controller hierarchy changes)
		if (!target || !min || !max) {
			const key = contextKey || `${target?.name ?? 'undef'}|${min?.name ?? 'undef'}|${max?.name ?? 'undef'}|${this.handedness}`;
			if (!this._missingNodeLogKeys.has(key)) {
				this._missingNodeLogKeys.add(key);
				console.warn(`[InputProfile] Missing nodes for transform (hand=${this.handedness}) target=${target?.name ?? 'undef'} min=${min?.name ?? 'undef'} max=${max?.name ?? 'undef'} key=${key}`);
			}
			return;
		}
		
		vec3.lerp(
			_TempVec,
			min.getPositionWorld(minTemp),
			max.getPositionWorld(maxTemp),
			value,
		);
		target.setTranslationWorld(_TempVec);

		quat.lerp(_TempQuat, min.rotationWorld, max.rotationWorld, value);
		quat.normalize(_TempQuat, _TempQuat);
		target.rotationWorld = _TempQuat;
	}

	mapGamepadInput() {
		const components = this.ProfileJSON.layouts[this.handedness].components;
		if (!components) return;

		for (const i in components) {
			if (components.hasOwnProperty(i)) {
				const component = components[i];
				const visualResponses = component.visualResponses;

				for (const j in visualResponses) {
					if (visualResponses.hasOwnProperty) {
						const visualResponse = visualResponses[j];
						const target =
							this._gamepadObjects[visualResponse.valueNodeName];
						const min =
							this._gamepadObjects[visualResponse.minNodeName];
						const max =
							this._gamepadObjects[visualResponse.maxNodeName];

						// Skip components that don't have all three required nodes
						// (may be intentionally filtered in getGamepadObjectsFromProfile if not in model)
						if (!target || !min || !max) {
							continue;
						}

						this.assignTransform(
							target,
							min,
							max,
							this.getGamepadValue(component, visualResponse),
							`${visualResponse.valueNodeName}|${visualResponse.minNodeName}|${visualResponse.maxNodeName}|${this.handedness}`,
						);
					}
				}
			}
		}
	}

	getGamepadValue(component: any, visualResponse: any) {
		if (visualResponse.valueNodeProperty === "transform") {
			switch (component.type) {
				case "button":
					return this.gamepad.buttons[component.gamepadIndices.button]
						.pressed;

				case "thumbstick":
					if (visualResponse.componentProperty === "button") {
						return this.gamepad.buttons[
							component.gamepadIndices.button
						].pressed;
					} else if (visualResponse.componentProperty === "xAxis") {
						return (
							(this.gamepad.axes[component.gamepadIndices.xAxis] +
								1) /
							2
						);
					} else if (visualResponse.componentProperty === "yAxis") {
						return (
							(this.gamepad.axes[component.gamepadIndices.yAxis] +
								1) /
							2
						);
					} else {
						Logger.warn(`Unidentified componentProperty: ${visualResponse.componentProperty}`, MODULE);
					}
				case "trigger":
					return this.gamepad.buttons[component.gamepadIndices.button]
						.value;

				case "squeeze":
					return this.gamepad.buttons[component.gamepadIndices.button]
						.value;
			}
		}
	}
}
